import React, { useEffect, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { analyticsMonitor } from '../utils/analyticsMonitor';
import { type ConsentStatus, consentManager } from '../utils/consentManager';

interface ConsentOption {
  id: string;
  title: string;
  description: string;
  required: boolean;
}

const consentOptions: ConsentOption[] = [
  {
    id: 'functional',
    title: 'Cookies Funcionales',
    description: 'Necesarias para el funcionamiento básico del sitio.',
    required: true,
  },
  {
    id: 'analytics',
    title: 'Cookies Analíticas',
    description: 'Nos ayudan a mejorar el sitio analizando su uso.',
    required: false,
  },
  {
    id: 'advertising',
    title: 'Cookies Publicitarias',
    description: 'Permiten mostrar anuncios relevantes a sus intereses.',
    required: false,
  },
];

const ConsentButton: React.FC<{
  onClick: () => void;
  variant: 'primary' | 'secondary';
  children: React.ReactNode;
}> = ({ onClick, variant, children }) => (
  <button
    onClick={onClick}
    className={twMerge(
      'px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105',
      variant === 'primary'
        ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
        : 'bg-white text-gray-800 hover:bg-gray-100 border border-gray-300'
    )}
  >
    {children}
  </button>
);

const EnhancedConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selections, setSelections] = useState<ConsentStatus>({
    functional: true,
    analytics: false,
    advertising: false,
  });
  const [bannerAnimation, setBannerAnimation] = useState<
    'animate-slide-up' | 'animate-slide-down' | ''
  >('');

  useEffect(() => {
    const initializeConsent = () => {
      const currentConsent = consentManager.getConsentStatus();
      const hasAnyConsent = Object.values(currentConsent).some(value => value);

      if (!hasAnyConsent) {
        setShowBanner(true);
        setBannerAnimation('animate-slide-up');
        setSelections({
          functional: true,
          analytics: false,
          advertising: false,
        });
      } else {
        setSelections(currentConsent);
        setShowBanner(false);
      }
    };

    // Suscribirse a cambios en el consentimiento
    const unsubscribe = consentManager.onConsentChange(() => {
      const currentConsent = consentManager.getConsentStatus();
      setSelections(currentConsent);
    });

    // Inicializar con un pequeño retraso
    const timer = setTimeout(initializeConsent, 100);

    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  const handleAnimatedHide = (callback?: () => void) => {
    setBannerAnimation('animate-slide-down');
    setTimeout(() => {
      setShowBanner(false);
      if (callback) callback();
    }, 500);
  };

  const handleAcceptAll = () => {
    const newConsent: ConsentStatus = {
      functional: true,
      analytics: true,
      advertising: true,
    };

    handleAnimatedHide(() => {
      consentManager.updateConsent(newConsent);
      analyticsMonitor.trackMetric('consent_action', 1, {
        action: 'accept_all',
        functional: 'true',
        analytics: 'true',
        advertising: 'true',
      });
    });
  };

  const handleSavePreferences = () => {
    handleAnimatedHide(() => {
      consentManager.updateConsent(selections);
      analyticsMonitor.trackMetric('consent_action', 1, {
        action: 'save_preferences',
        functional: selections.functional ? 'true' : 'false',
        analytics: selections.analytics ? 'true' : 'false',
        advertising: selections.advertising ? 'true' : 'false',
      });
    });
  };

  const handleReject = () => {
    const newConsent: ConsentStatus = {
      functional: true,
      analytics: false,
      advertising: false,
    };

    handleAnimatedHide(() => {
      consentManager.updateConsent(newConsent);
      analyticsMonitor.trackMetric('consent_action', 1, {
        action: 'reject_all',
        functional: 'true',
        analytics: 'false',
        advertising: 'false',
      });
    });
  };

  const toggleOption = (optionId: string) => {
    if (consentOptions.find(opt => opt.id === optionId)?.required) {
      return;
    }

    setSelections(prev => ({
      ...prev,
      [optionId]: !prev[optionId as keyof ConsentStatus],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className={twMerge(
          'w-full max-w-2xl mx-4 bg-white rounded-xl shadow-2xl transform transition-all duration-500',
          bannerAnimation === 'animate-slide-up' && 'translate-y-0 opacity-100',
          bannerAnimation === 'animate-slide-down' && 'translate-y-full opacity-0',
          !bannerAnimation && 'translate-y-0 opacity-100'
        )}
      >
        <div className="p-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 sm:truncate">
                Preferencias de Privacidad
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Utilizamos cookies para mejorar su experiencia. Seleccione sus preferencias a
                continuación.
              </p>
            </div>

            <div className="mt-4 flex md:mt-0 md:ml-4">
              <ConsentButton onClick={() => setShowDetails(!showDetails)} variant="secondary">
                {showDetails ? 'Ocultar Detalles' : 'Mostrar Detalles'}
              </ConsentButton>
            </div>
          </div>

          {showDetails && (
            <div className="mt-4 space-y-4">
              {consentOptions.map(option => (
                <div key={option.id} className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id={option.id}
                      type="checkbox"
                      checked={selections[option.id as keyof ConsentStatus]}
                      onChange={() => toggleOption(option.id)}
                      disabled={option.required}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor={option.id} className="font-medium text-gray-700">
                      {option.title}
                      {option.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <p className="text-gray-500">{option.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row sm:space-x-4 space-y-3 sm:space-y-0">
            <ConsentButton onClick={handleReject} variant="secondary">
              Rechazar No Esenciales
            </ConsentButton>
            <ConsentButton onClick={handleSavePreferences} variant="primary">
              Guardar Preferencias
            </ConsentButton>
            <ConsentButton onClick={handleAcceptAll} variant="primary">
              Aceptar Todo
            </ConsentButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedConsentBanner;
